"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useQuery, useAction } from "convex/react";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { api } from "../../../../convex/_generated/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckIcon } from "@/components/icons";
import { usePageTitle } from "@/lib/usePageTitle";
import { useEntitlementVerify } from "@/hooks/useEntitlementVerify";
import {
  getServerLimitForSubscription,
  type SubscriptionStatus,
  type SubscriptionTier,
} from "@/lib/billing/plans";

function provisionStatusBadge(status: string) {
  switch (status) {
    case "active":
      return <Badge variant="success">Running</Badge>;
    case "pending":
      return <Badge variant="secondary">Provisioning...</Badge>;
    case "error":
      return <Badge variant="destructive">Error</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function tierLabel(tier: SubscriptionTier) {
  if (tier === "free") return "Free";
  return `Founder ${tier.charAt(0).toUpperCase()}${tier.slice(1)}`;
}

function requestedPlanLabel(plan: string | null) {
  if (!plan) return null;
  if (plan === "starter") return "Founder Starter";
  if (plan === "plus") return "Founder Plus";
  if (plan === "pro") return "Founder Pro";
  if (plan === "basic") return "Basic";
  if (plan === "enterprise") return "Enterprise";
  return null;
}

function statusLabel(status: SubscriptionStatus) {
  if (status === "none") return "No active subscription";
  return status.replace(/_/g, " ");
}

function appendCheckoutSuccessFlag(nextPath: string) {
  const [pathAndQuery, hash = ""] = nextPath.split("#", 2);
  const [pathname, queryString = ""] = pathAndQuery.split("?", 2);
  const params = new URLSearchParams(queryString);
  params.set("checkout", "success");
  const nextQuery = params.toString();
  return `${pathname}${nextQuery ? `?${nextQuery}` : ""}${hash ? `#${hash}` : ""}`;
}

export default function BillingSuccessPage() {
  usePageTitle("Checkout Success");

  // Fire a fresh Polar.sh entitlement check immediately on mount.
  // This warms the polarEntitlementCache so the SubscriptionGate on the
  // destination route has an authoritative Polar API result ready, rather
  // than relying solely on the webhook-populated Convex state.
  useEntitlementVerify();

  const searchParams = useSearchParams();
  const router = useRouter();
  const summary = useQuery(api.billing.getMySummary);
  const provisioningOverview = useQuery(api.dashboard.getPostPurchaseOverview);
  const getStripeSessionStatus = useAction(api.actions.getStripeSessionStatus);
  const [countdown, setCountdown] = useState<number | null>(null);

  // Stripe session validation state
  const stripeSessionId = searchParams.get("session_id");
  const provider = searchParams.get("provider");
  const isStripe = provider === "stripe" || (!!stripeSessionId && !searchParams.get("checkout_id"));
  const [stripeStatus, setStripeStatus] = useState<{
    status: string;
    paymentStatus: string;
    customerEmail: string | null;
    plan: string | null;
  } | null>(null);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  // Validate Stripe session on mount if session_id is present
  useEffect(() => {
    if (!stripeSessionId || !isStripe) return;
    let cancelled = false;
    setStripeLoading(true);
    getStripeSessionStatus({ sessionId: stripeSessionId })
      .then((result) => {
        if (cancelled) return;
        if (result.ok && result.data) {
          setStripeStatus(result.data);
        } else {
          setStripeError(result.ok ? "Unknown error" : result.error.message);
        }
      })
      .catch((err) => {
        if (!cancelled) setStripeError(err instanceof Error ? err.message : "Failed to validate session");
      })
      .finally(() => { if (!cancelled) setStripeLoading(false); });
    return () => { cancelled = true; };
  }, [stripeSessionId, isStripe, getStripeSessionStatus]);

  const checkoutId = searchParams.get("checkout_id") || stripeSessionId;
  const requestedPlan = requestedPlanLabel(searchParams.get("plan"));

  // Validate the next redirect: must be a relative path starting with "/" to
  // prevent open redirect attacks.
  const rawNext = searchParams.get("next");
  const safeNext =
    rawNext && /^\/[^/]/.test(rawNext) ? rawNext : "/dashboard";

  const tier = (summary?.tier ?? "free") as SubscriptionTier;
  const status = (summary?.status ?? "none") as SubscriptionStatus;
  const isActivated = tier !== "free" && (status === "active" || status === "trialing");
  const successRedirectNext = appendCheckoutSuccessFlag(safeNext);
  const continueHref = isActivated ? successRedirectNext : safeNext;
  const continuePathname = continueHref.split("?")[0];

  // Auto-redirect: once the webhook has confirmed the subscription is active,
  // start a 3-second countdown then navigate to the original destination.
  useEffect(() => {
    if (!isActivated) return;
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          router.replace(successRedirectNext);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isActivated, router, successRedirectNext]);
  const serverLimit = getServerLimitForSubscription(tier, status);
  const renewalDate = summary?.currentPeriodEnd
    ? new Date(summary.currentPeriodEnd).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;
  const trialEndDate = summary?.trialEndsAt
    ? new Date(summary.trialEndsAt).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Card className="border-green-300 bg-green-50/60 dark:border-green-700 dark:bg-green-950/20">
        <CardHeader>
          <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-600/15 text-green-700 dark:text-green-300">
            <CheckIcon className="h-5 w-5" />
          </div>
          <CardTitle>Checkout Complete</CardTitle>
          <CardDescription>
            {isStripe
              ? "Stripe accepted your payment. We\u2019re syncing your subscription status now."
              : "Polar accepted your checkout. We\u2019re syncing your subscription status now."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted">
          {requestedPlan && (
            <p>
              Selected plan: <span className="font-medium text-foreground">{requestedPlan}</span>
            </p>
          )}
          {checkoutId && (
            <p>
              Checkout reference: <span className="font-mono text-foreground">{checkoutId}</span>
            </p>
          )}
          {isStripe && stripeLoading && (
            <p className="text-muted">Validating Stripe session...</p>
          )}
          {isStripe && stripeError && (
            <p className="text-red-600">Session validation failed: {stripeError}</p>
          )}
          {isStripe && stripeStatus && (
            <p>
              Payment status:{" "}
              <span className="font-medium capitalize text-foreground">
                {stripeStatus.paymentStatus}
              </span>
            </p>
          )}
        </CardContent>
      </Card>

      {summary === undefined ? (
        <Card>
          <CardHeader>
            <CardTitle>Confirming Subscription</CardTitle>
            <CardDescription>
              Waiting for your latest billing state from Convex.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Skeleton className="h-4 w-44" />
            <Skeleton className="h-4 w-64" />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2">
              <CardTitle>Subscription Status</CardTitle>
              <Badge variant={isActivated ? "success" : "secondary"}>
                {isActivated ? "Synced" : "Pending"}
              </Badge>
            </div>
            <CardDescription>
              {isActivated
                ? "Your account now reflects the latest webhook state."
                : "The payment provider callback arrived. Waiting for webhook state to finalize this account."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted">
            <p>
              Plan: <span className="font-medium text-foreground">{tierLabel(tier)}</span>
            </p>
            <p>
              Status: <span className="font-medium capitalize text-foreground">{statusLabel(status)}</span>
            </p>
            {serverLimit > 0 && (
              <p>
                Organization limit: <span className="font-medium text-foreground">{serverLimit}</span>
              </p>
            )}
            {status === "trialing" && trialEndDate && (
              <p>
                Trial ends on <span className="font-medium text-foreground">{trialEndDate}</span>.
              </p>
            )}
            {status !== "trialing" && renewalDate && (
              <p>
                Next renewal: <span className="font-medium text-foreground">{renewalDate}</span>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {isActivated && provisioningOverview?.server && (
        <Card>
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2">
              <CardTitle>Game Server</CardTitle>
              {provisionStatusBadge(provisioningOverview.server.provisionStatus)}
            </div>
            <CardDescription>
              {provisioningOverview.server.provisionStatus === "pending"
                ? "Your game server is being provisioned. This usually takes 1\u20132 minutes."
                : provisioningOverview.server.provisionStatus === "active"
                  ? "Your game server is ready to connect."
                  : provisioningOverview.server.provisionStatus === "error"
                    ? "There was an issue provisioning your server. Our team has been notified."
                    : "Checking server status\u2026"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted">
            {provisioningOverview.server.serverIp && (
              <p>
                Server IP:{" "}
                <span className="font-mono font-medium text-foreground">
                  {provisioningOverview.server.serverIp}:{provisioningOverview.server.serverPort}
                </span>
              </p>
            )}
            <p>
              Region:{" "}
              <span className="font-medium text-foreground">
                {provisioningOverview.server.vultrRegion}
              </span>
            </p>
            <p>
              Plan:{" "}
              <span className="font-medium text-foreground">
                {provisioningOverview.server.vultrPlanSku}
              </span>
            </p>
            {provisioningOverview.server.provisionError && (
              <p className="text-red-600">
                Error: {provisioningOverview.server.provisionError}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {isActivated && countdown !== null && countdown > 0 && (
        <p className="text-sm text-muted">
          Redirecting in {countdown}s&hellip;{" "}
          <button
            onClick={() => {
              setCountdown(null);
              router.replace(successRedirectNext);
            }}
            className="text-primary hover:underline"
          >
            Go now
          </button>
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <Link href="/dashboard/settings/billing">
          <Button>Manage Billing</Button>
        </Link>
        <Link href={continueHref}>
          <Button variant="outline">
            {continuePathname === "/dashboard" ? "Return to Dashboard" : "Continue →"}
          </Button>
        </Link>
      </div>
    </div>
  );
}
